import { LatLng } from 'api/public/place/types';
import { API } from 'shared/constants/api';
import { PAGE_SIZE, PAGE_INDEX } from 'shared/constants/common';
import { PaginationArgs } from 'shared/types';
import { publicApi } from './api';
import type {
  B2BCategory,
  B2BItem,
  ItemSearchParams,
  B2BStore,
  B2BTag,
  B2BNearByStore,
} from './types';

export const b2bApi = publicApi.injectEndpoints({
  endpoints: (builder) => ({
    getStoreDetail: builder.query<B2BStore, { id: B2BStore['store_id'] }>({
      query: (params) => ({ url: API.public.b2b.store_detail, params }),
      providesTags: (_, _id) => [
        {
          type: 'B2BStores' as const,
          _id,
        },
      ],
    }),
    getStoresByIds: builder.query<B2BStore[], { ids: string[] }>({
      query: ({ ids }) => ({
        url: API.public.b2b.stores,
        params: { ids: [...ids].join(',') },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ store_id }) => ({
                type: 'B2BStores' as const,
                store_id,
              })),
              { type: 'B2BStores', _id: 'LIST' },
            ]
          : [{ type: 'B2BStores', _id: 'LIST' }],
    }),
    getCategories: builder.query<B2BCategory[], void>({
      query: () => ({ url: API.public.b2b.activated_categories }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'Categories' as const,
                _id,
              })),
              { type: 'Categories', _id: 'LIST' },
            ]
          : [{ type: 'Categories', _id: 'LIST' }],
    }),
    getItemsByIds: builder.query<B2BItem[], { ids: string[] }>({
      query: ({ ids }) => ({
        url: API.public.b2b.items,
        params: { ids: [...ids].join(',') },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ store_id }) => ({
                type: 'B2BItems' as const,
                store_id,
              })),
              { type: 'B2BItems', _id: 'LIST' },
            ]
          : [{ type: 'B2BItems', _id: 'LIST' }],
    }),
    getPromoItems: builder.query<B2BItem[], LatLng & PaginationArgs>({
      query: ({ size = PAGE_SIZE, page = PAGE_INDEX, ...params }) => ({
        url: API.public.b2b.promo_items,
        params: {
          ...params,
          page,
          size,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'B2BItems' as const,
                _id,
              })),
              { type: 'B2BItems', _id: 'LIST' },
            ]
          : [{ type: 'B2BItems', _id: 'LIST' }],
    }),
    getNearByItems: builder.query<
      B2BItem[],
      LatLng & PaginationArgs & { tags?: number }
    >({
      query: ({ size = PAGE_SIZE, page = PAGE_INDEX, ...params }) => ({
        url: API.public.b2b.nearby_items,
        params: {
          ...params,
          page,
          size,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'B2BItems' as const,
                _id,
              })),
              { type: 'B2BItems', _id: 'LIST' },
            ]
          : [{ type: 'B2BItems', _id: 'LIST' }],
    }),
    getNearByStores: builder.query<
      B2BNearByStore[],
      LatLng & PaginationArgs & { tags?: number }
    >({
      query: ({ size = PAGE_SIZE, page = PAGE_INDEX, ...params }) => ({
        url: API.public.b2b.nearby_stores,
        params: {
          ...params,
          page,
          size,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'B2BNearByStores' as const,
                _id,
              })),
              { type: 'B2BNearByStores', _id: 'LIST' },
            ]
          : [{ type: 'B2BNearByStores', _id: 'LIST' }],
    }),
    searchItem: builder.query<B2BItem[], ItemSearchParams>({
      query: ({ size = PAGE_SIZE, page = PAGE_INDEX, ...params }) => ({
        url: API.public.b2b.search_items,
        params: {
          ...params,
          page,
          size,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'B2BItems' as const,
                _id,
              })),
              { type: 'B2BItems', _id: 'LIST' },
            ]
          : [{ type: 'B2BItems', _id: 'LIST' }],
    }),
    getItemsByCategory: builder.query<
      B2BItem[],
      Pick<B2BCategory, 'slug'> & Partial<LatLng> & PaginationArgs
    >({
      query: ({ slug, size = PAGE_SIZE, page = PAGE_INDEX, ...params }) => ({
        url: API.public.b2b.items_by_category(slug),
        params: {
          ...params,
          page,
          size,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'B2BItems' as const,
                _id,
              })),
              { type: 'B2BItems', _id: 'LIST' },
            ]
          : [{ type: 'B2BItems', _id: 'LIST' }],
    }),
    getStoresByCategory: builder.query<
      B2BItem[],
      Pick<B2BCategory, 'slug'> & Partial<LatLng> & PaginationArgs
    >({
      query: ({ slug, size = PAGE_SIZE, page = PAGE_INDEX, ...params }) => ({
        url: API.public.b2b.stores_by_category(slug),
        params: {
          ...params,
          page,
          size,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'B2BStores' as const,
                _id,
              })),
              { type: 'B2BStores', _id: 'LIST' },
            ]
          : [{ type: 'B2BStores', _id: 'LIST' }],
    }),
    getItemDetail: builder.query<B2BItem, B2BItem['slug']>({
      query: (slug) => ({ url: API.public.b2b.item_detail(slug) }),
      providesTags: (_, _id) => [
        {
          type: 'B2BItems' as const,
          _id,
        },
      ],
    }),
    getHomeTags: builder.query<B2BTag[], void>({
      query: () => ({ url: API.public.b2b.tags }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'Tags' as const,
                _id,
              })),
              { type: 'Tags', _id: 'LIST' },
            ]
          : [{ type: 'Tags', _id: 'LIST' }],
      transformResponse: (response: B2BTag[]) => {
        return response
          .filter(
            (tag) =>
              tag.screen.includes('home') &&
              tag.tag.includes('B2B_') &&
              tag.enable
          )
          .sort((a, b) => a.order - b.order);
      },
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetPromoItemsQuery,
  useGetNearByItemsQuery,
  useGetNearByStoresQuery,
  useGetItemsByCategoryQuery,
  useGetItemDetailQuery,
  useGetStoreDetailQuery,
  useGetStoresByIdsQuery,
  useGetItemsByIdsQuery,
  useGetHomeTagsQuery,
  useGetStoresByCategoryQuery,
} = b2bApi;
